import React, { useEffect, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { toast } from 'react-toastify';
import { styled, Switch, Tooltip } from '@mui/material';
import { Column } from 'primereact/column';
import { Check, Close, PlayCircleFilled } from '@mui/icons-material';
import { LoadingRowsComponent } from '../../../components/commons/LoadingRows/LoadingRows';
import {
  IAddEvent,
  IEvent,
  ITimeType
} from '../../../types/events/eventsInterface';
import {
  useChangeEventStatusMutation,
  useEditEventMutation,
  useGetEvenDetailsMutation,
  usePostEventExecuteMutation
} from '../../../services/events/eventService';
import Strings from '../../../i18n';
import { colors } from '../../../constants/colors';
import { ContainerTable } from '../../locations/locationsList/locationListStyles';
import { eventTypesStr } from '../../../constants/eventEnums';
import { maxCharactersString } from '../../../utils/commonUtils';
import { PickEventTime } from '../../../components/events/pickEventTime/pickEventTime';
import { EventUtils } from '../../../utils/eventUtils';
import { CommandsTypesItemEnum } from '../../../constants/commonEnums';

interface IEventsListTemplateProps {
  selectedEvents: any[];
  setSelectedEvents: (selectedEvents: any[]) => void;
  eventsToExecute: number[];
  isLoading: boolean;
  events: IEvent[];
  reloadTable: () => void;
}
export const EventsListTemplate = ({
  selectedEvents,
  setSelectedEvents,
  isLoading,
  events,
  eventsToExecute,
  reloadTable
}: IEventsListTemplateProps) => {
  const [
    postEventExecute,
    { isLoading: isLoadingExecute, isSuccess: isSuccessExecute }
  ] = usePostEventExecuteMutation();
  const [
    changeEventStatus,
    { isLoading: isLoadingChangeStatus, isSuccess: isSuccessChangeStatus }
  ] = useChangeEventStatusMutation();
  const [editEvent, { isSuccess: isSuccessEditEvent }] = useEditEventMutation();
  const [event, setEvent] = useState<IEvent>();
  const [getEvent, { data: eventDetails }] = useGetEvenDetailsMutation();
  const [showEventTimePicker, setShowEventTimePicker] = useState(false);
  const StyledSpanDisabled = styled('span')({
    fontSize: '12px',
    fontWeight: 'bold',
    backgroundColor: colors.mediumGray,
    padding: '5px',
    borderRadius: '5px',
    color: colors.white
  });

  const getOverrideSport = (rowData: IEvent) => {
    return rowData.overrideSport ? <Close color="error" /> : <div />;
  };

  const getColumnId = (rowData: IEvent) => {
    return <span>{`${rowData.scriptId} (${rowData.eventId})`}</span>;
  };

  const getEnabledColumn = (rowData: IEvent) => {
    if (!rowData.isEnabled) {
      return <StyledSpanDisabled>Disabled</StyledSpanDisabled>;
    }

    return eventsToExecute.includes(rowData.eventId) ? (
      <Check color="success" />
    ) : (
      <Close color="error" />
    );
  };

  useEffect(() => {
    if (eventDetails) {
      setEvent(eventDetails);
    }
  }, [eventDetails]);

  useEffect(() => {
    if (isSuccessEditEvent) {
      toast.success(Strings.events.eventStatusChangedSuccessfully);
      reloadTable();
    }
  }, [isSuccessEditEvent]);

  useEffect(() => {
    if (!showEventTimePicker) {
      setEvent(undefined);
    }
  }, [showEventTimePicker]);
  useEffect(() => {
    if (isSuccessExecute) {
      toast.success(Strings.events.eventExecutedSuccessfully);
    }
  }, [isSuccessExecute]);
  useEffect(() => {
    if (isSuccessChangeStatus) {
      toast.success(Strings.events.eventStatusChangedSuccessfully);
      reloadTable();
    }
  }, [isSuccessChangeStatus]);
  const executeEvent = (eventId: number) => {
    postEventExecute(eventId);
  };
  const getActionsColumn = (rowData: IEvent) => {
    return (
      <div>
        <Tooltip title={Strings.events.executeThisEvent}>
          <PlayCircleFilled
            onClick={(e) => {
              e.stopPropagation();
              executeEvent(rowData.eventId);
            }}
            style={{ cursor: 'pointer' }}
          />
        </Tooltip>
      </div>
    );
  };
  const getWeekDayColumn = (rowData: IEvent) => {
    return (
      <span>
        {rowData.weekDay === 'Every Day' ? 'ALL WEEK' : rowData.weekDay}
      </span>
    );
  };

  const getSummaryColumn = (rowData: IEvent) => {
    const rawString = rowData?.summary || '';

    const listItems = rawString.split('\r\n');

    return (
      <ul>
        {listItems.map((item, index) => (
          <Tooltip title={item}>
            <li>{maxCharactersString(item, 40)}</li>
          </Tooltip>
        ))}
      </ul>
    );
  };
  const getEventTimeColumn = (rowData: IEvent) => {
    return (
      <button
        type="button"
        onClick={() => {
          getEvent(rowData.eventId);
          setShowEventTimePicker(true);
          setEvent(rowData);
        }}
        style={{
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: 'pointer'
        }}
      >
        {rowData.localExecutionTime}{' '}
        {rowData.timeType === 1 ? '(Time)' : `(${rowData.timeTypeStr})`}
      </button>
    );
  };
  const parseTimeToMilliseconds = (time: string): number => {
    const [timePart, period] = time.split(' ');
    const [hoursStr, minutesStr] = timePart.split(':');

    let hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr, 10);

    if (period === 'PM' && hours !== 12) {
      hours += 12;
    } else if (period === 'AM' && hours === 12) {
      hours = 0;
    }

    return hours * 60 * 60 * 1000 + minutes * 60 * 1000;
  };
  const eventTimeSortFunction = (e: any) => {
    const { data, order } = e;
    const sortedData = [...data];

    sortedData.sort((a: IEvent, b: IEvent) => {
      const valueA = parseTimeToMilliseconds(a.localExecutionTime);
      const valueB = parseTimeToMilliseconds(b.localExecutionTime);

      return order * (valueA - valueB);
    });

    return sortedData;
  };
  const getEventTypeColumn = (rowData: IEvent) => {
    return eventTypesStr[
      rowData?.lightingStatusId as keyof typeof eventTypesStr
    ];
  };

  const setTimeEvent = (data: ITimeType) => {
    if (event) {
      let targets: any[] = [];

      if (event.lightIds && event.lightIds.length) {
        targets = event.lightIds.map((light: any) => ({
          id: light.lightZoneId,
          type: light.isZone
            ? CommandsTypesItemEnum.Zone
            : CommandsTypesItemEnum.Light
        }));
      } else if (event.groupId) {
        targets = [{ id: event.groupId, type: CommandsTypesItemEnum.Group }];
      }
      const eventToUpdate = {
        ...event,
        timeType: data.timeType,
        hour: data.hour,
        minute: data.minute,
        offsetMinutes: data.offsetMinutes,
        lightType: event?.lightType ?? 0,
        targets
      } as unknown as IAddEvent;
      editEvent(eventToUpdate);
    }
  };
  return (
    <>
      {isLoading && <LoadingRowsComponent />}
      {!isLoading && events && (
        <ContainerTable component="main">
          <DataTable
            selection={selectedEvents}
            selectionMode="multiple"
            onSelectionChange={(e) => setSelectedEvents(e.value)}
            className="mt-3"
            rowHover
            value={events}
            size="small"
            scrollable
            scrollHeight="flex"
            paginator={events && events.length >= 50}
            selectionPageOnly
            rows={50}
            rowsPerPageOptions={[5, 10, 20, 50, events ? events.length : 50]}
            tableStyle={{ padding: 0, margin: 0, fontSize: '14px' }}
            metaKeySelection
          >
            <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />
            <Column field="ID" body={getColumnId} header="ID" />
            <Column
              field="overrideSport"
              header="Sport"
              body={getOverrideSport}
            />
            <Column field="isEnabled" header="Today" body={getEnabledColumn} />
            <Column field="name" header="Event Name" sortable />
            <Column
              field="lightingStatusId"
              header="Event Type"
              sortable
              body={getEventTypeColumn}
            />
            <Column field="dateTypeStr" header="Date Range" sortable />
            <Column
              field="eventTime"
              header="Event Time - Current"
              sortable
              body={getEventTimeColumn}
              sortFunction={eventTimeSortFunction}
            />
            <Column
              field="summary"
              header="Summary"
              sortable
              body={getSummaryColumn}
            />
            <Column field="sortOrder" header="Order" sortable />
            <Column
              field="weekDay"
              header="Week Day"
              sortable
              body={getWeekDayColumn}
            />
            <Column field="" header="Actions" body={getActionsColumn} />
          </DataTable>
        </ContainerTable>
      )}
      {event && (
        <PickEventTime
          showEventTimePicker={showEventTimePicker}
          setShowEventTimePicker={setShowEventTimePicker}
          timeType={{
            timeType: event.timeType ?? 0,
            hour: event.hour ?? 0,
            minute: event.minute ?? 0,
            offsetMinutes: event.offsetMinutes ?? 0
          }}
          onOk={(info) => setTimeEvent(info)}
        />
      )}
    </>
  );
};
